import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/auth/auth-context";

type WorkspacePlan = "starter" | "pro" | "team";
type PlanInterval = "MONTHLY" | "YEARLY";

type UpgradeEstimateResponse = {
  targetPlanPrice: number;
  originalAmountDue: number;
  finalAmountDue: number;
  appliedPromo: {
    code: string;
    benefitSummary: string;
    checkoutRequired: boolean;
    benefitType: "DISCOUNT" | "GRANT";
  } | null;
};

const PLAN_PRICE_USD: Record<Exclude<WorkspacePlan, "starter">, Record<PlanInterval, number>> = {
  pro: {
    MONTHLY: 49,
    YEARLY: 468,
  },
  team: {
    MONTHLY: 64,
    YEARLY: 612,
  },
};

export default function UpgradePlan() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refreshMe, user, loading: authLoading } = useAuth();
  const handledStatusRef = useRef<string | null>(null);
  const starterPreviewRequestRef = useRef(0);

  const status = (searchParams.get("status") || "").toLowerCase();
  const initialTargetPlan = (searchParams.get("targetPlan") || "pro").toLowerCase();
  const initialInterval = searchParams.get("interval") === "YEARLY" ? "YEARLY" : "MONTHLY";

  const currentPlan = (user?.currentPlan?.toLowerCase() as WorkspacePlan | undefined) ?? null;
  const currentInterval = user?.planInterval ?? null;
  const waitingForWorkspaceState = authLoading || !currentPlan || !currentInterval;

  const availablePlans = useMemo(() => {
    if (currentPlan === "team") return [] as WorkspacePlan[];
    if (currentPlan === "pro") return ["team"] as WorkspacePlan[];
    if (currentPlan === "starter") return ["pro", "team"] as WorkspacePlan[];
    return [] as WorkspacePlan[];
  }, [currentPlan]);

  const [targetPlan, setTargetPlan] = useState<WorkspacePlan>(
    initialTargetPlan === "team" ? "team" : initialTargetPlan === "pro" ? "pro" : "starter",
  );
  const [targetInterval, setTargetInterval] = useState<PlanInterval>(initialInterval);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const [promoError, setPromoError] = useState("");
  const [starterPreview, setStarterPreview] = useState<UpgradeEstimateResponse | null>(null);
  const [loadingStarterPreview, setLoadingStarterPreview] = useState(false);

  const annualOnlyUpgradePath =
    currentPlan !== null && currentPlan !== "starter" && currentInterval === "YEARLY";
  const targetPlanIsAllowed = availablePlans.includes(targetPlan);
  const hasAvailableUpgrade = availablePlans.length > 0;
  const targetIntervalValue = annualOnlyUpgradePath ? "YEARLY" : targetInterval;
  const isStarterUpgradePath = currentPlan === "starter" && targetPlanIsAllowed && targetPlan !== "starter";

  useEffect(() => {
    if (!status || handledStatusRef.current === status) {
      return;
    }

    handledStatusRef.current = status;
    const next = new URLSearchParams(searchParams);
    next.delete("status");
    setSearchParams(next, { replace: true });

    if (status === "success") {
      void refreshMe().finally(() => {
        toast({
          title: t("pages.upgradePlan.title"),
          description: t("pages.upgradePlan.updated"),
        });
        navigate("/settings", { replace: true });
      });
      return;
    }

    if (status === "cancelled") {
      toast({
        title: t("pages.upgradePlan.cancelledTitle"),
        description: t("pages.upgradePlan.cancelledDescription"),
        variant: "destructive",
      });
    }
  }, [navigate, refreshMe, searchParams, setSearchParams, status, t, toast]);

  useEffect(() => {
    if (waitingForWorkspaceState) return;

    if (!targetPlanIsAllowed) {
      setTargetPlan(availablePlans[0] ?? "team");
    }
  }, [availablePlans, targetPlanIsAllowed, waitingForWorkspaceState]);

  useEffect(() => {
    if (annualOnlyUpgradePath && targetInterval !== "YEARLY") {
      setTargetInterval("YEARLY");
    }
  }, [annualOnlyUpgradePath, targetInterval]);

  useEffect(() => {
    if (!isStarterUpgradePath) {
      setStarterPreview(null);
      setLoadingStarterPreview(false);
      return;
    }

    const requestId = ++starterPreviewRequestRef.current;
    setLoadingStarterPreview(true);

    void apiRequest<UpgradeEstimateResponse>("/companies/me/upgrade-estimate", {
      method: "POST",
      body: {
        targetPlanId: targetPlan,
        targetPlanInterval: targetIntervalValue,
        promoCode: appliedPromoCode ?? undefined,
      },
    })
      .then((response) => {
        if (starterPreviewRequestRef.current !== requestId) return;
        setStarterPreview(response);
        setPromoError("");
      })
      .catch((error) => {
        if (starterPreviewRequestRef.current !== requestId) return;
        setStarterPreview(null);
        if (appliedPromoCode) {
          setPromoError(error instanceof Error ? error.message : t("common.tryAgain"));
        }
      })
      .finally(() => {
        if (starterPreviewRequestRef.current === requestId) {
          setLoadingStarterPreview(false);
        }
      });
  }, [appliedPromoCode, isStarterUpgradePath, t, targetIntervalValue, targetPlan]);

  const applyPromoCode = () => {
    const normalized = promoCode.trim().toUpperCase();
    if (!normalized) {
      setPromoError(t("pages.upgradePlan.enterPromoFirst"));
      return;
    }

    setAppliedPromoCode(normalized);
    setPromoCode(normalized);
    setPromoError("");
  };

  const clearAppliedPromo = () => {
    setAppliedPromoCode(null);
    setPromoError("");
  };

  const continueCheckout = async () => {
    if (!currentPlan || !targetPlanIsAllowed) {
      return;
    }

    setCreatingCheckout(true);
    try {
      const submittedPromoCode = appliedPromoCode || promoCode.trim().toUpperCase() || undefined;
      const data = await apiRequest<{
        url: string;
        mode: "stripe" | "credit";
        checkoutRequired?: boolean;
        message?: string;
      }>("/companies/me/upgrade-checkout-session", {
        method: "POST",
        body: {
          targetPlanId: targetPlan,
          targetPlanInterval: targetIntervalValue,
          promoCode: submittedPromoCode,
          successPath: `/upgrade-plan?status=success&targetPlan=${encodeURIComponent(targetPlan)}&interval=${encodeURIComponent(
            targetIntervalValue,
          )}`,
          cancelPath: `/upgrade-plan?status=cancelled&targetPlan=${encodeURIComponent(targetPlan)}&interval=${encodeURIComponent(
            targetIntervalValue,
          )}`,
          baseUrl: window.location.origin,
        },
      });

      window.location.href = data.url;
    } catch (error) {
      toast({
        title: t("createAccount.unableToContinue"),
        description: error instanceof Error ? error.message : t("common.tryAgain"),
        variant: "destructive",
      });
      setCreatingCheckout(false);
    }
  };

  const currentPlanLabel = currentPlan ? t(`plans.${currentPlan}`) : "—";
  const targetPlanLabel = targetPlanIsAllowed ? t(`plans.${targetPlan}`) : "—";
  const continueLabel = t("common.continue");
  const starterBasePrice =
    isStarterUpgradePath && targetPlan !== "starter"
      ? PLAN_PRICE_USD[targetPlan][targetIntervalValue]
      : null;
  const starterFinalPrice = starterPreview?.finalAmountDue ?? starterBasePrice;
  const showStarterPriceCard = isStarterUpgradePath && starterBasePrice !== null;
  const formatMoney = (value: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t("pages.upgradePlan.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("pages.upgradePlan.selectionDescription")}
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="rounded-xl border bg-card p-5 space-y-4 lg:col-span-1">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t("pages.upgradePlan.targetPlan")}</label>
            <select
              value={targetPlan}
              onChange={(event) => setTargetPlan(event.target.value as WorkspacePlan)}
              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              disabled={waitingForWorkspaceState || !hasAvailableUpgrade}
            >
              {availablePlans.map((plan) => (
                <option key={plan} value={plan}>
                  {t(`plans.${plan}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t("pages.upgradePlan.billingCycle")}</label>
            <select
              value={targetIntervalValue}
              onChange={(event) => setTargetInterval(event.target.value as PlanInterval)}
              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              disabled={waitingForWorkspaceState || !hasAvailableUpgrade}
            >
              {!annualOnlyUpgradePath ? (
                <option value="MONTHLY">{t("pricing.monthly")}</option>
              ) : null}
              <option value="YEARLY">{t("pages.upgradePlan.yearlySave")}</option>
            </select>
            {annualOnlyUpgradePath ? (
              <p className="text-xs text-muted-foreground">{t("pages.upgradePlan.annualOnlyRule")}</p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t("pages.upgradePlan.promoCode")}</label>
            <div className="flex items-center gap-2">
              <Input
                value={promoCode}
                onChange={(event) => {
                  const normalized = event.target.value.toUpperCase();
                  setPromoCode(normalized);
                  setPromoError("");
                  if (appliedPromoCode && normalized.trim() !== appliedPromoCode) {
                    setAppliedPromoCode(null);
                  }
                }}
                placeholder={t("pages.upgradePlan.promoPlaceholder")}
                disabled={!hasAvailableUpgrade}
              />
              <Button type="button" variant="outline" onClick={applyPromoCode} disabled={!hasAvailableUpgrade}>
                {t("pages.upgradePlan.apply")}
              </Button>
            </div>
            {promoError ? <p className="text-xs text-destructive">{promoError}</p> : null}
            {appliedPromoCode ? (
              <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                <span>{appliedPromoCode}</span>
                <button type="button" className="font-medium hover:underline" onClick={clearAppliedPromo}>
                  {t("common.clear")}
                </button>
              </div>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {t("pages.upgradePlan.promoFieldHelp")}
            </p>
          </div>
        </section>

        <section className="rounded-xl border bg-card p-5 lg:col-span-2">
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border bg-background p-3">
                <p className="text-xs text-muted-foreground">{t("pages.upgradePlan.currentPlan")}</p>
                <p className="text-sm font-semibold">
                  {currentPlanLabel}{" "}
                  {currentInterval ? `(${currentInterval === "YEARLY" ? t("payment.annual") : t("payment.monthly")})` : ""}
                </p>
              </div>
              <div className="rounded-md border bg-background p-3">
                <p className="text-xs text-muted-foreground">{t("pages.upgradePlan.targetPlan")}</p>
                <p className="text-sm font-semibold">
                  {targetPlanLabel}{" "}
                  {targetPlanIsAllowed
                    ? `(${targetIntervalValue === "YEARLY" ? t("payment.annual") : t("payment.monthly")})`
                    : ""}
                </p>
              </div>
            </div>

            {!hasAvailableUpgrade ? (
              <div className="rounded-md border border-muted bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
                {t("pages.upgradePlan.noUpgradeAvailable")}
              </div>
            ) : (
              <>
                {showStarterPriceCard ? (
                  <div className="rounded-md border bg-background p-4 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t("pages.upgradePlan.targetPlanPrice", { defaultValue: "Target plan price" })}</span>
                      <span className="font-semibold">{formatMoney(starterBasePrice)}</span>
                    </div>
                    {appliedPromoCode ? (
                      <div className="flex items-start justify-between gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">{t("pages.upgradePlan.priceAfterPromo", { defaultValue: "Price after promo" })}</p>
                          {starterPreview?.appliedPromo?.benefitSummary ? (
                            <p className="mt-1 text-xs text-emerald-700">{starterPreview.appliedPromo.benefitSummary}</p>
                          ) : null}
                        </div>
                        <span className="font-semibold text-emerald-700">
                          {loadingStarterPreview || starterFinalPrice === null
                            ? t("pages.upgradePlan.calculating")
                            : formatMoney(starterFinalPrice)}
                        </span>
                      </div>
                    ) : null}
                    <p className="text-xs text-muted-foreground">
                      {t("pages.upgradePlan.starterFixedPriceNote", {
                        defaultValue: "Starter upgrades use the fixed plan price shown here. Final confirmation still happens in Stripe.",
                      })}
                    </p>
                  </div>
                ) : (
                  <div className="rounded-md border border-accent/20 bg-accent/5 px-3 py-2 text-sm text-muted-foreground">
                    {t("pages.upgradePlan.finalBillingInStripe")}
                  </div>
                )}

                <div className="rounded-md border bg-background p-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t("payment.nextStep")}</span>
                    <span className="font-medium">{t("pages.upgradePlan.stripeConfirmationPath")}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t("payment.promoOutcome")}</span>
                    <span className="font-medium">
                      {appliedPromoCode
                        ? loadingStarterPreview && isStarterUpgradePath
                          ? t("pages.upgradePlan.calculating")
                          : t("pages.upgradePlan.promoCheckedInStripe")
                        : t("payment.standardOutcome")}
                    </span>
                  </div>
                </div>
              </>
            )}

            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => navigate("/settings")}>
                {t("login.cancel")}
              </Button>
              <Button
                onClick={() => void continueCheckout()}
                disabled={creatingCheckout || waitingForWorkspaceState || !targetPlanIsAllowed || !hasAvailableUpgrade || Boolean(promoError) || (isStarterUpgradePath && Boolean(appliedPromoCode) && loadingStarterPreview)}
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                <Lock className="mr-2 h-4 w-4" />
                {creatingCheckout ? t("pages.upgradePlan.redirecting") : continueLabel}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
